/**
 * V_CMS_DEEP_REVAMP F26 — admin reports client.
 *
 * Sistemsel raporlama endpoint'lerine tipli erişim.
 *   GET /admin/reports/daily-usage?days=30
 *   GET /admin/reports/sectors?days=30
 *   GET /admin/reports/plan-funnel
 *   GET /admin/reports/health-trend?days=7
 *   GET /admin/reports/users/{id}/report?days=30
 */
import { apiClient } from "./client";

export type DailyUsagePoint = {
  date: string;
  contents: number;
  cost_usd: number;
  calls: number;
  tokens: number;
  words: number;
};

export type DailyUsageResponse = {
  days: number;
  start: string;
  end: string;
  series: DailyUsagePoint[];
  totals: {
    contents: number;
    cost_usd: number;
    calls: number;
    tokens: number;
    words: number;
  };
};

export type SectorReportRow = {
  industry_id: string;
  name: string;
  user_count: number;
  content_count: number;
  ready_count: number;
  cost_usd: number;
  word_count: number;
};

export type PlanFunnelResponse = {
  funnel: {
    registered: number;
    active: number;
    setup_completed: number;
    subscribed_active: number;
  };
  by_plan: Array<{
    plan_id: string;
    slug: string;
    name: string;
    active: number;
    trialing: number;
    canceled: number;
  }>;
};

export type HealthTrendPoint = {
  date: string;
  total: number;
  critical: number;
  warning: number;
  info: number;
};

export type HealthTrendResponse = {
  days: number;
  series: HealthTrendPoint[];
  total: number;
};

export type UserReportResponse = {
  user_id: string;
  days: number;
  start: string;
  end: string;
  series: Array<{
    date: string;
    contents: number;
    cost_usd: number;
    words: number;
  }>;
  totals: {
    contents: number;
    cost_usd: number;
    words: number;
  };
  subscriptions: Array<Record<string, unknown>>;
  anomalies: Array<{
    id: string;
    anomaly_type: string;
    severity: string;
    message: string | null;
    created_at: string;
  }>;
};

export const reportsApi = {
  dailyUsage: async (days = 30): Promise<DailyUsageResponse> => {
    const { data } = await apiClient.get("/api/admin/reports/daily-usage", {
      params: { days },
    });
    return data;
  },
  sectors: async (days = 30): Promise<SectorReportRow[]> => {
    const { data } = await apiClient.get("/api/admin/reports/sectors", {
      params: { days },
    });
    return data;
  },
  planFunnel: async (): Promise<PlanFunnelResponse> => {
    const { data } = await apiClient.get("/api/admin/reports/plan-funnel");
    return data;
  },
  healthTrend: async (days = 7): Promise<HealthTrendResponse> => {
    const { data } = await apiClient.get("/api/admin/reports/health-trend", {
      params: { days },
    });
    return data;
  },
  userReport: async (userId: string, days = 30): Promise<UserReportResponse> => {
    const { data } = await apiClient.get(
      `/api/admin/reports/users/${userId}/report`,
      { params: { days } },
    );
    return data;
  },

  // ── Custom report (pivot) ──
  datasets: async (): Promise<{ datasets: PivotDataset[] }> => {
    const { data } = await apiClient.get("/api/admin/reports/datasets");
    return data;
  },
  pivot: async (body: PivotRequest): Promise<PivotResponse> => {
    const { data } = await apiClient.post("/api/admin/reports/pivot", body);
    return data;
  },
};

// ── Pivot / custom report types ──

export type PivotDimension = { key: string; label: string };
export type PivotMeasure = {
  key: string;
  label: string;
  is_money: boolean;
  agg?: "sum" | "avg" | "distinct" | "max";
};

export type PivotDataset = {
  key: string;
  label: string;
  date_filtered: boolean;
  dimensions: PivotDimension[];
  measures: PivotMeasure[];
  default_group: string[];
  default_measures: string[];
};

export type PivotRequest = {
  dataset: string;
  group_by: string[];
  measures: string[];
  days?: number;
  limit?: number;
  column?: string | null; // çapraz tablo sütun ekseni
  filters?: Record<string, string[]>;
};

export type PivotResponse = {
  dataset: string;
  mode: "table" | "crosstab";
  group_by: string[];
  dimension_meta: PivotDimension[];
  rows: Array<Record<string, string | number>>;
  total_rows: number;
  truncated: boolean;
  fetched: number;
  // table mode
  measures?: string[];
  measure_meta?: PivotMeasure[];
  totals?: Record<string, number>; // ölçü key → genel toplam/ortalama
  // crosstab mode
  column?: string;
  column_meta?: PivotDimension;
  column_values?: { key: string; label: string }[];
  value_measure?: PivotMeasure;
  column_totals?: Record<string, number>; // col::<val> → toplam, __total → genel
  row_total_supported?: boolean; // avg/distinct'te satır toplamı anlamsız
};
